import React, { useState, useRef, useEffect } from 'react';
import { Box, Textarea, Text } from '@chakra-ui/react';

/**
 * SQL Syntax Highlighter Component with Auto-Sizing
 */
const SQLHighlighter = ({ 
    value, 
    onChange, 
    placeholder, 
    rows = 15, 
    maxLength = 10000,
    isDisabled = false,
    bg = 'gray.50',
    showHighlightButton = true,
    autoHighlight = false,
    autoSize = false,
    minRows = 5,
    maxRows = 40,
    ...props 
}) => {
    // State management for highlighting and editor control
    const [currentValue, setCurrentValue] = useState(value || '');
    const [highlightedContent, setHighlightedContent] = useState('');
    const [isHighlighted, setIsHighlighted] = useState(false);
    const [isHighlighting, setIsHighlighting] = useState(false);
    const [highlightSuccess, setHighlightSuccess] = useState(false);
    const textareaRef = useRef(null);
    const highlightRef = useRef(null);

    // Comprehensive SQL Server keywords covering all T-SQL features
    const sqlKeywords = [
        // Core DQL keywords
        'select', 'from', 'where', 'join', 'inner', 'left', 'right', 'full', 'outer', 'cross',
        'on', 'and', 'or', 'not', 'in', 'exists', 'between', 'like', 'is', 'null', 'isnull',
        'group', 'by', 'having', 'order', 'asc', 'desc', 'union', 'all', 'distinct', 'top',
        'limit', 'offset', 'fetch', 'next', 'rows', 'only', 'with', 'as', 'case', 'when',
        'then', 'else', 'end', 'over', 'partition', 'into',
        
        // DML keywords
        'insert', 'update', 'delete', 'merge', 'using', 'matched', 'output', 'inserted', 'deleted',
        'values', 'set', 'truncate',
        
        // DDL keywords
        'create', 'alter', 'drop', 'table', 'view', 'index', 'procedure', 'function', 'trigger',
        'database', 'schema', 'constraint', 'primary', 'key', 'foreign', 'references', 'unique',
        'check', 'default', 'identity', 'clustered', 'nonclustered', 'columnstore',
        
        // Data types
        'int', 'bigint', 'smallint', 'tinyint', 'bit', 'decimal', 'numeric', 'money', 'smallmoney',
        'float', 'real', 'datetime', 'datetime2', 'smalldatetime', 'date', 'time', 'datetimeoffset',
        'timestamp', 'char', 'varchar', 'nchar', 'nvarchar', 'text', 'ntext', 'binary', 'varbinary',
        'image', 'uniqueidentifier', 'xml', 'json', 'sql_variant', 'geography', 'geometry',
        
        // Built-in functions
        'count', 'sum', 'avg', 'min', 'max', 'cast', 'convert', 'coalesce', 'nullif', 'len',
        'substring', 'charindex', 'patindex', 'upper', 'lower', 'ltrim', 'rtrim', 'trim',
        'replace', 'stuff', 'reverse', 'concat', 'datepart', 'dateadd', 'datediff', 'year',
        'month', 'day', 'getdate', 'getutcdate', 'sysdatetime', 'sysutcdatetime', 'current_timestamp',
        'abs', 'ceiling', 'floor', 'round', 'power', 'sqrt', 'log', 'exp', 'rand',
        
        // Window functions
        'row_number', 'rank', 'dense_rank', 'ntile', 'lead', 'lag', 'first_value', 'last_value',
        'percent_rank', 'cume_dist', 'percentile_cont', 'percentile_disc',
        
        // Control flow
        'if', 'else', 'begin', 'end', 'while', 'break', 'continue', 'goto', 'return', 'waitfor',
        'try', 'catch', 'throw', 'raiserror', 'print', 'declare', 'set',
        
        // Transaction keywords
        'transaction', 'commit', 'rollback', 'save', 'savepoint', 'tran',
        
        // Performance and locking hints
        'nolock', 'readuncommitted', 'readcommitted', 'repeatableread', 'serializable',
        'readpast', 'updlock', 'xlock', 'tablock', 'tablockx', 'paglock', 'rowlock',
        'forceseek', 'forcescan', 'maxdop', 'recompile', 'keepfixed', 'keepfixedplan',
        'optimize', 'parameterization', 'robust', 'simple',
        
        // System functions and metadata
        'scope_identity', 'ident_current', 'ident_incr', 'ident_seed', 'newid', 'newsequentialid',
        'user_name', 'system_user', 'session_user', 'host_name', 'app_name', 'db_name',
        'object_name', 'object_id', 'schema_name', 'schema_id', 'user_id', 'database_id',
        'serverproperty', 'databasepropertyex', 'filegroup_name', 'file_name',
        
        // Operators and special keywords
        'any', 'some', 'contains', 'freetext', 'containstable', 'freetexttable', 'openquery',
        'openrowset', 'opendatasource', 'pivot', 'unpivot', 'apply', 'tablesample',
        'cube', 'rollup', 'grouping', 'grouping_id', 'compute', 'for', 'browse',
        
        // Common CTEs and advanced features
        'cte', 'recursive', 'xmlpath', 'xmlroot', 'xmlelement', 'xmlattributes', 'xmlforest',
        'json_value', 'json_query', 'json_modify', 'openjson', 'isjson', 'string_split',
        'string_agg', 'translate', 'formatmessage', 'choose', 'iif'
    ];

    // Create a Set for O(1) keyword lookup performance
    const keywordSet = new Set(sqlKeywords);

    /**
     * Calculate optimal rows for content auto-sizing
     * Determines appropriate height based on SQL content length and structure
     */
    const calculateOptimalRows = () => {
        if (!autoSize || !currentValue) return rows;
        
        // Count actual line breaks in content
        const lineCount = currentValue.split('\n').length;
        
        // Add buffer for better readability (SQL queries often need more space)
        const calculatedRows = Math.max(lineCount + 2, minRows);
        
        // Ensure within reasonable limits
        return Math.min(calculatedRows, maxRows);
    };

    /**
     * Generate highlighted HTML content from SQL text with proper escaping
     */
    const generateHighlightedHTML = (text) => {
        if (!text) return '';

        // Protection system for strings and comments
        const protectedItems = [];
        let processedText = text;

        // Step 1: Extract and protect single-quoted strings
        processedText = processedText.replace(/'(?:[^']|'')*'/g, (match) => {
            const index = protectedItems.length;
            protectedItems.push({
                type: 'string',
                content: match,
                placeholder: `__PROTECTED_STRING_${index}__`
            });
            return `__PROTECTED_STRING_${index}__`;
        });

        // Step 2: Extract and protect double-quoted identifiers
        processedText = processedText.replace(/"(?:[^"]|"")*"/g, (match) => {
            const index = protectedItems.length;
            protectedItems.push({
                type: 'identifier',
                content: match,
                placeholder: `__PROTECTED_IDENTIFIER_${index}__`
            });
            return `__PROTECTED_IDENTIFIER_${index}__`;
        });

        // Step 3: Extract and protect single-line comments
        processedText = processedText.replace(/--[^\r\n]*/g, (match) => {
            const index = protectedItems.length;
            protectedItems.push({
                type: 'comment',
                content: match,
                placeholder: `__PROTECTED_COMMENT_${index}__`
            });
            return `__PROTECTED_COMMENT_${index}__`;
        });

        // Step 4: Extract and protect multi-line comments
        processedText = processedText.replace(/\/\*[\s\S]*?\*\//g, (match) => {
            const index = protectedItems.length;
            protectedItems.push({
                type: 'comment',
                content: match,
                placeholder: `__PROTECTED_COMMENT_${index}__`
            });
            return `__PROTECTED_COMMENT_${index}__`;
        });

        // Step 5: Extract and protect bracket identifiers
        processedText = processedText.replace(/\[[^\]]*\]/g, (match) => {
            const index = protectedItems.length;
            protectedItems.push({
                type: 'bracket_identifier',
                content: match,
                placeholder: `__PROTECTED_BRACKET_${index}__`
            });
            return `__PROTECTED_BRACKET_${index}__`;
        });

        // Step 6: Escape HTML characters BEFORE highlighting to prevent tag conflicts
        processedText = processedText
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');

        // Step 7: Highlight numbers (integers and decimals) 
        processedText = processedText.replace(/\b\d+(?:\.\d+)?\b/g, (match) => {
            return `<span class="sql-number">${match}</span>`;
        });

        // Step 8: Highlight SQL keywords with enhanced word boundary detection
        const tokens = processedText.split(/(\s+|[^\w]+)/);
        
        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            if (token && /^\w+$/.test(token)) {
                const lowerToken = token.toLowerCase();
                if (keywordSet.has(lowerToken)) {
                    tokens[i] = `<span class="sql-keyword">${token.toUpperCase()}</span>`;
                }
            }
        }
        
        processedText = tokens.join('');

        // Step 9: Restore protected content with appropriate styling and proper HTML escaping
        protectedItems.forEach((item) => {
            // Escape the content properly for HTML display
            const escapedContent = item.content
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

            let cssClass;
            switch (item.type) {
                case 'string':
                    cssClass = 'sql-string';
                    break;
                case 'comment':
                    cssClass = 'sql-comment';
                    break;
                case 'identifier':
                case 'bracket_identifier':
                    cssClass = 'sql-identifier';
                    break;
                default:
                    cssClass = 'sql-default';
            }

            // Replace placeholder with properly formatted span
            processedText = processedText.replace(
                item.placeholder,
                `<span class="${cssClass}">${escapedContent}</span>`
            );
        });

        // Step 10: Convert whitespace for HTML display while preserving structure
        processedText = processedText
            .replace(/\r?\n/g, '<br>')
            .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
            .replace(/  /g, '&nbsp;&nbsp;');

        return processedText;
    };

    /**
     * Apply automatic highlighting for view mode
     * Used when autoHighlight prop is true
     */
    const applyAutoHighlighting = (text) => {
        if (!text.trim()) return;
        
        try {
            const highlighted = generateHighlightedHTML(text);
            setHighlightedContent(highlighted);
            setIsHighlighted(true);
        } catch (error) {
            console.error('Auto-highlighting failed:', error);
        }
    };

    /**
     * Handle text change without highlighting in edit mode
     */
    const handleTextChange = (e) => {
        const newValue = e.target.value;
        setCurrentValue(newValue);
        setHighlightSuccess(false); // Reset highlight success indicator
        
        // If currently highlighted, turn off highlighting to show normal text (unless auto-highlight mode)
        if (isHighlighted && !autoHighlight) {
            setIsHighlighted(false);
            setHighlightedContent('');
        }
        
        onChange(newValue);
    };

    /**
     * Handle manual highlight button click
     * Applies SQL syntax highlighting when user requests it
     */
    const handleHighlightSQL = () => {
        if (!currentValue.trim()) {
            return;
        }
        
        setIsHighlighting(true);
        
        try {
            const highlighted = generateHighlightedHTML(currentValue);
            setHighlightedContent(highlighted);
            setIsHighlighted(true);
            
            // Show success indicator
            setHighlightSuccess(true);
            setTimeout(() => setHighlightSuccess(false), 2000); // Clear after 2 seconds
            
        } catch (error) {
            console.error('SQL highlighting failed:', error);
        } finally {
            setIsHighlighting(false);
        }
    };

    /**
     * Toggle between highlighted and normal view
     * Allows users to switch between syntax highlighting and plain text
     */
    const toggleHighlighting = () => {
        if (isHighlighted) {
            setIsHighlighted(false);
            setHighlightedContent('');
        } else {
            handleHighlightSQL();
        }
    };

    /**
     * Synchronize scroll position when in highlighted mode
     */
    const handleScroll = () => {
        if (isHighlighted && textareaRef.current && highlightRef.current) {
            const textarea = textareaRef.current;
            const highlight = highlightRef.current;
            highlight.style.transform = `translate(-${textarea.scrollLeft}px, -${textarea.scrollTop}px)`;
        }
    };

    // Initialize value when prop changes
    useEffect(() => {
        if (value !== currentValue) {
            setCurrentValue(value || '');
            
            // Auto-highlight if enabled, otherwise reset highlighting
            if (autoHighlight && value?.trim()) {
                applyAutoHighlighting(value);
            } else {
                setIsHighlighted(false);
                setHighlightedContent('');
            }
        }
    }, [value, autoHighlight]);

    // Apply auto-highlighting on mount if enabled
    useEffect(() => {
        if (autoHighlight && currentValue?.trim()) {
            applyAutoHighlighting(currentValue);
        }
    }, [autoHighlight]);

    return (
        <Box position="relative" width="100%">
            {/* Enhanced CSS styles for SQL syntax highlighting */}
            <style>
                {`
                    .sql-editor-container {
                        position: relative;
                        font-family: 'Consolas', 'Monaco', 'Courier New', 'SF Mono', monospace;
                        font-size: 14px;
                        line-height: 1.5;
                        border-radius: 6px;
                        overflow: hidden;
                    }
                    
                    .sql-highlight-overlay {
                        position: absolute;
                        top: 2px;
                        left: 2px;
                        right: 2px;
                        bottom: 2px;
                        padding: 8px 12px;
                        margin: 0;
                        border: none;
                        background: transparent;
                        color: inherit;
                        pointer-events: none;
                        overflow: hidden;
                        white-space: pre-wrap;
                        word-wrap: break-word;
                        word-break: break-word;
                        z-index: 1;
                        box-sizing: border-box;
                        font-family: inherit;
                        font-size: inherit;
                        line-height: inherit;
                        border-radius: 4px;
                    }
                    
                    .sql-textarea {
                        position: relative;
                        z-index: 2;
                        font-family: inherit;
                        font-size: inherit;
                        line-height: inherit;
                        resize: ${autoSize ? 'none' : 'vertical'};
                        border: 2px solid #E2E8F0;
                        border-radius: 6px;
                    }
                    
                    .sql-textarea:focus {
                        border-color: #3182CE;
                        box-shadow: 0 0 0 1px #3182CE;
                    }
                    
                    /* Highlighted mode styles */
                    .sql-textarea.highlighted {
                        background: transparent !important;
                        color: transparent !important;
                        caret-color: #2D3748;
                    }
                    
                    .sql-textarea.highlighted:focus {
                        background: transparent !important;
                        color: transparent !important;
                    }
                    
                    .sql-textarea.highlighted::selection {
                        background: rgba(49, 130, 206, 0.3);
                    }
                    
                    /* SQL Syntax Highlighting Colors - Light Theme */
                    .sql-keyword {
                        color: #0066CC;
                        font-weight: bold;
                    }
                    
                    .sql-string {
                        color: #DD1144;
                    }
                    
                    .sql-comment {
                        color: #999988;
                        font-style: italic;
                    }
                    
                    .sql-number {
                        color: #009999;
                        font-weight: 500;
                    }
                    
                    .sql-identifier {
                        color: #445588;
                        font-weight: 500;
                    }
                    
                    /* Dark theme styles */
                    .chakra-ui-dark .sql-textarea {
                        border-color: #4A5568;
                    }
                    
                    .chakra-ui-dark .sql-textarea:focus {
                        border-color: #63B3ED;
                        box-shadow: 0 0 0 1px #63B3ED;
                    }
                    
                    .chakra-ui-dark .sql-textarea.highlighted {
                        caret-color: #E2E8F0;
                    }
                    
                    /* SQL Syntax Highlighting Colors - Dark Theme */
                    .chakra-ui-dark .sql-keyword {
                        color: #66B3FF;
                        font-weight: bold;
                    }
                    
                    .chakra-ui-dark .sql-string {
                        color: #FFB3BA;
                    }
                    
                    .chakra-ui-dark .sql-comment {
                        color: #CCCCCC;
                        font-style: italic;
                    }
                    
                    .chakra-ui-dark .sql-number {
                        color: #66FFCC;
                        font-weight: 500;
                    }
                    
                    .chakra-ui-dark .sql-identifier {
                        color: #C9A9DD;
                        font-weight: 500;
                    }
                    
                    /* Button styles */
                    .highlight-button {
                        font-size: 12px;
                        color: #3182CE;
                        background: none;
                        border: none;
                        cursor: pointer;
                        text-decoration: underline;
                        padding: 4px 8px;
                        border-radius: 4px;
                        transition: background-color 0.2s;
                    }
                    
                    .highlight-button:hover:not(:disabled) {
                        background-color: #E6F3FF;
                    }
                    
                    .highlight-button:disabled {
                        color: #A0AEC0;
                        cursor: not-allowed;
                        text-decoration: none;
                    }
                    
                    .highlight-success {
                        color: #38A169;
                        font-weight: medium;
                    }
                    
                    .highlighted-indicator {
                        color: #0066CC;
                        font-weight: medium;
                    }
                `}
            </style>
            
            <Box className="sql-editor-container">
                {/* Highlighted background overlay - only visible when highlighted */}
                {isHighlighted && (
                    <Box
                        ref={highlightRef}
                        className="sql-highlight-overlay"
                        dangerouslySetInnerHTML={{ __html: highlightedContent }}
                    />
                )}
                
                {/* SQL Textarea with Auto-Sizing Support */}
                <Textarea
                    ref={textareaRef}
                    className={`sql-textarea ${isHighlighted ? 'highlighted' : ''}`}
                    value={currentValue}
                    onChange={handleTextChange}
                    onScroll={handleScroll}
                    placeholder={placeholder}
                    rows={autoSize ? calculateOptimalRows() : rows}
                    maxLength={maxLength}
                    isDisabled={isDisabled}
                    bg={isHighlighted ? 'transparent' : bg}
                    spellCheck={false}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    {...props}
                />
            </Box>
            
            {/* Footer with Highlight Button and Status */}
            <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                <Text fontSize="xs" color="gray.500">
                    {currentValue.length}{maxLength ? `/${maxLength}` : ''} characters
                    {autoSize && ` • Auto-sized: ${calculateOptimalRows()} rows`}
                </Text>
                
                <Box display="flex" alignItems="center" gap={3}>
                    {/* Highlight Status Indicators */}
                    {isHighlighted && (
                        <Text fontSize="xs" className="highlighted-indicator">
                            {autoHighlight ? '✓ Auto-Highlighted' : '✓ Highlighted'}
                        </Text>
                    )}
                    
                    {highlightSuccess && !autoHighlight && (
                        <Text fontSize="xs" className="highlight-success">
                            ✓ Applied
                        </Text>
                    )}
                    
                    {/* Highlight Button - only show if not auto-highlight mode */}
                    {showHighlightButton && !autoHighlight && (
                        <button
                            className="highlight-button"
                            onClick={toggleHighlighting}
                            disabled={isDisabled || isHighlighting || !currentValue.trim()}
                        >
                            {isHighlighting ? 'Highlighting...' : 
                             isHighlighted ? 'Remove Highlighting' : 'Highlight SQL'}
                        </button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default SQLHighlighter;